import { useContext, useEffect, useMemo } from 'react';
import { Circle, CircleMarker, MapContainer, TileLayer, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

import { cn } from '@/lib/utils';
import { ColorModeContext } from '../../../../App';
import type { TradeMapPoint, TradeOrigin } from '../../../../common/hooks/use-trade-api.hook';
import { PinTone, statusPinTone } from '../trade-format';

/**
 * Theme colours as values: leaflet writes them into SVG attributes, where a
 * CSS variable is not reliably resolved.
 */
function readPalette(): Record<PinTone | 'background', string> {
    const style = typeof window !== 'undefined' ? getComputedStyle(document.documentElement) : null;
    const v = (name: string, fallback: string) => style?.getPropertyValue(name).trim() || fallback;
    return {
        foreground: v('--foreground', '#1c1917'),
        muted: v('--muted-foreground', '#78716c'),
        success: v('--success', '#16a34a'),
        warning: v('--warning', '#d97706'),
        background: v('--background', '#ffffff'),
    };
}

/** Fits the circle once it is known, and again when the radius changes. */
const FitRadius = ({ origin, radiusKm }: { origin: TradeOrigin; radiusKm: number }) => {
    const map = useMap();
    useEffect(() => {
        const bounds = L.latLng(origin.lat, origin.lng).toBounds(radiusKm * 2000);
        map.fitBounds(bounds, { padding: [8, 8] });
    }, [map, origin.lat, origin.lng, radiusKm]);
    return null;
};

/**
 * Where the professionals are, around the vendor.
 *
 * The leaflet the analytics cities map already uses (no new dependency),
 * drawn with circle markers rather than icons: hundreds of pins stay light,
 * and colour carries only the status.
 */
export const TradeMap = ({
    origin,
    radiusKm,
    points,
    selectedId,
    onSelect,
    originLabel,
}: {
    origin: TradeOrigin;
    radiusKm: number;
    points: TradeMapPoint[];
    selectedId?: string | null;
    onSelect: (id: string) => void;
    originLabel: string;
}) => {
    const { mode } = useContext(ColorModeContext);
    const palette = useMemo(readPalette, [mode]);
    const colour = (p: TradeMapPoint) => palette[statusPinTone(p.status)];
    // Best last, so the strongest prospects draw on top.
    const ordered = useMemo(() => [...points].sort((a, b) => a.score - b.score), [points]);

    return (
        <div
            className={cn(
                // isolate: leaflet's panes carry z-index 400+; without a stacking
                // context of its own the map paints over sheets and dialogs.
                'isolate h-full min-h-72 overflow-hidden rounded-xl border',
                // Grey tiles, so the only colour on the map is the status of a pin.
                '[&_.leaflet-tile-pane]:opacity-70 [&_.leaflet-tile-pane]:grayscale',
                mode === 'dark' && '[&_.leaflet-tile-pane]:invert',
            )}
            data-testid="trade-map"
        >
            <MapContainer center={[origin.lat, origin.lng]} zoom={10} scrollWheelZoom={false} className="h-full min-h-72 w-full">
                {/* OpenStreetMap's own tiles, credited as their policy asks: the Carto
                    basemaps the analytics map uses now answer "API key required". */}
                <TileLayer url="https://tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' maxZoom={19} />
                <FitRadius origin={origin} radiusKm={radiusKm} />
                <Circle
                    center={[origin.lat, origin.lng]}
                    radius={radiusKm * 1000}
                    pathOptions={{ color: palette.foreground, weight: 1, opacity: 0.35, fillColor: palette.foreground, fillOpacity: 0.02, dashArray: '4 4' }}
                />
                {ordered.map((p) => (
                    <CircleMarker
                        key={p._id}
                        center={[p.lat, p.lng]}
                        radius={p._id === selectedId ? 7 : 4 + Math.round(p.score / 40)}
                        pathOptions={{
                            color: p._id === selectedId ? palette.foreground : colour(p),
                            weight: p._id === selectedId ? 2 : 1,
                            fillColor: colour(p),
                            fillOpacity: p.status === 'new' ? 0.55 : 0.85,
                        }}
                        eventHandlers={{ click: () => onSelect(p._id) }}
                    >
                        <Tooltip direction="top" offset={[0, -4]}>{p.name}</Tooltip>
                    </CircleMarker>
                ))}
                <CircleMarker
                    center={[origin.lat, origin.lng]}
                    radius={6}
                    pathOptions={{ color: palette.background, weight: 2, fillColor: palette.foreground, fillOpacity: 1 }}
                >
                    <Tooltip direction="top" offset={[0, -6]} permanent>{originLabel}</Tooltip>
                </CircleMarker>
            </MapContainer>
        </div>
    );
};

export default TradeMap;
